// AGC: VoiceCommandHelpModal - First-time / recurring help overlay for voice commands
// AGC: Shows what the user can say (task, field, machinery, inventory, expense) with short examples
// AGC: Preference: "Don't show again" or "Show once a week" stored in localStorage

import React from 'react'
import { X } from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'

const STORAGE_KEY = 'voiceHelpPreference'

export type VoiceHelpPreference = 'never' | 'weekly'

export interface VoiceHelpStorage {
  preference: VoiceHelpPreference
  lastShownAt?: string // AGC: ISO date string for weekly logic
}

/** AGC: Default examples that match backend voice patterns and fill maximum fields */
const VOICE_EXAMPLES = [
  {
    key: 'task',
    titleKey: 'voice.help.addingTask',
    exampleKey: 'voice.help.exampleTask',
    example: 'Nova tarefa, nome Pulverizar talhão 5B, tipo pulverizar',
  },
  {
    key: 'field',
    titleKey: 'voice.help.addingField',
    exampleKey: 'voice.help.exampleField',
    example: 'Criar talhão, nome Pantar, área 10 hectares, cultivo soja',
  },
  {
    key: 'machinery',
    titleKey: 'voice.help.addingMachinery',
    exampleKey: 'voice.help.exampleMachinery',
    example: 'Registrar trator, marca John Deere, ano 2020, horímetro 500',
  },
  {
    key: 'inventory',
    titleKey: 'voice.help.addingInventory',
    exampleKey: 'voice.help.exampleInventory',
    example: 'Adicionar ao estoque, nome Glifosato, quantidade 50 litros',
  },
  {
    key: 'expense',
    titleKey: 'voice.help.addingExpense',
    exampleKey: 'voice.help.exampleExpense',
    example: 'Despesa 500 reais, combustível',
  },
] as const

interface VoiceCommandHelpModalProps {
  isVisible: boolean
  onClose: () => void
  onPreferenceChoose: (preference: VoiceHelpPreference) => void
}

/**
 * AGC: VoiceCommandHelpModal - Transparent centered modal with voice command examples
 * AGC: Renders when user opens voice (first time or once a week per preference)
 */
export const VoiceCommandHelpModal: React.FC<VoiceCommandHelpModalProps> = ({
  isVisible,
  onClose,
  onPreferenceChoose,
}) => {
  const { t } = useTranslation()

  if (!isVisible) return null

  const handleNeverAgain = () => {
    onPreferenceChoose('never')
    onClose()
  }

  const handleOnceAWeek = () => {
    onPreferenceChoose('weekly')
    onClose()
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      data-testid="voice-command-help-modal"
      role="dialog"
      aria-labelledby="voice-help-title"
      aria-modal="true"
    >
      {/* AGC: Transparent dark overlay - allows voice modal to show behind */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* AGC: Centered card - semi-transparent, readable */}
      <div
        className="relative w-full max-w-md max-h-[85vh] overflow-hidden rounded-2xl border border-white/20 bg-slate-900/95 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* AGC: Close button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-colors z-10"
          aria-label={t('common.close')}
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-5 pt-4 pb-4 overflow-y-auto max-h-[calc(85vh-140px)]">
          <h2
            id="voice-help-title"
            className="text-lg font-semibold text-white mb-4 pr-8"
          >
            {t('voice.help.title')}
          </h2>

          <p className="text-white/70 text-sm mb-4">
            {t('voice.help.subtitle')}
          </p>

          <div className="space-y-4">
            {VOICE_EXAMPLES.map((item) => (
              <div
                key={item.key}
                className="p-3 rounded-xl bg-white/5 border border-white/10"
              >
                <p className="text-white font-medium text-sm mb-1">
                  {t(item.titleKey)}
                </p>
                <p className="text-emerald-300/95 text-xs italic">
                  &quot;{t(item.exampleKey, item.example)}&quot;
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* AGC: Shiny preference buttons */}
        <div className="p-4 pt-2 border-t border-white/10 bg-slate-900/80 space-y-2">
          <button
            type="button"
            onClick={handleNeverAgain}
            className="w-full py-3 px-4 rounded-xl font-medium text-sm transition-all duration-200
              bg-gradient-to-r from-amber-500/90 to-orange-500/90 hover:from-amber-400 hover:to-orange-400
              text-white shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40
              border border-amber-400/30 hover:border-amber-300/50
              active:scale-[0.98]"
            data-testid="voice-help-never-again"
          >
            {t('voice.help.dontShowAnymore')}
          </button>
          <button
            type="button"
            onClick={handleOnceAWeek}
            className="w-full py-3 px-4 rounded-xl font-medium text-sm transition-all duration-200
              bg-gradient-to-r from-emerald-500/90 to-teal-500/90 hover:from-emerald-400 hover:to-teal-400
              text-white shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40
              border border-emerald-400/30 hover:border-emerald-300/50
              active:scale-[0.98]"
            data-testid="voice-help-once-a-week"
          >
            {t('voice.help.showOnceAWeek')}
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * AGC: Load voice help preference from localStorage (per browser/device)
 */
export function getVoiceHelpPreference(): VoiceHelpStorage | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as VoiceHelpStorage
  } catch {
    return null
  }
}

/**
 * AGC: Save voice help preference and optionally update lastShownAt for weekly
 */
export function setVoiceHelpPreference(value: VoiceHelpStorage): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value))
  } catch {
    // AGC: Ignore quota/private mode
  }
}

const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000

/**
 * AGC: Whether we should show the voice help modal this time
 * AGC: Show if: never set (first time), or preference is weekly and 7+ days since lastShownAt
 */
export function shouldShowVoiceHelp(): boolean {
  const stored = getVoiceHelpPreference()
  if (!stored) return true
  if (stored.preference === 'never') return false
  if (stored.preference === 'weekly') {
    if (!stored.lastShownAt) return true
    const last = new Date(stored.lastShownAt).getTime()
    return Date.now() - last >= ONE_WEEK_MS
  }
  return false
}

/**
 * AGC: Mark help as shown now (update lastShownAt for weekly)
 */
export function markVoiceHelpShown(): void {
  const stored = getVoiceHelpPreference()
  if (!stored || stored.preference !== 'weekly') return
  setVoiceHelpPreference({
    ...stored,
    lastShownAt: new Date().toISOString(),
  })
}

export default VoiceCommandHelpModal
